/**
 * Admin panel queries and mutations (F9).
 *
 * Each mutation invalidates the query it could have changed: a taxonomy write
 * invalidates the vocabulary it edited, a settings write the settings, a user
 * change the user list, and a demo load/clear invalidates everything — demo
 * data touches every entity, so a stale list anywhere would misrepresent it.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 7.13, 7.14
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AdminSettings,
  AdminUser,
  CreateTaxonomyRequest,
  ListTaxonomyQuery,
  SaveBandThresholdsRequest,
  SaveStallThresholdsRequest,
  TaxonomyTerm,
  UpdateTaxonomyRequest,
} from '@nexus/contracts';

import { queryKeys } from '../../../lib/query-keys';

import * as adminApi from './admin.api';

// ── Taxonomy ────────────────────────────────────────────────────────────────

export function useTaxonomy(query: ListTaxonomyQuery): UseQueryResult<TaxonomyTerm[]> {
  return useQuery({
    queryKey: queryKeys.admin.taxonomy(query),
    queryFn: () => adminApi.listTaxonomy(query),
  });
}

/** Invalidates every taxonomy list — a create or delete changes the set. */
function useInvalidateTaxonomy(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: [...queryKeys.admin.all, 'taxonomy'],
    });
  };
}

export function useCreateTaxonomy(): UseMutationResult<
  TaxonomyTerm,
  Error,
  CreateTaxonomyRequest
> {
  const invalidate = useInvalidateTaxonomy();
  return useMutation({
    mutationFn: (body: CreateTaxonomyRequest) => adminApi.createTaxonomy(body),
    onSuccess: invalidate,
  });
}

export function useUpdateTaxonomy(
  id: string,
): UseMutationResult<TaxonomyTerm, Error, UpdateTaxonomyRequest> {
  const invalidate = useInvalidateTaxonomy();
  return useMutation({
    mutationFn: (body: UpdateTaxonomyRequest) => adminApi.updateTaxonomy(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteTaxonomy(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateTaxonomy();
  return useMutation({
    mutationFn: (id: string) => adminApi.deleteTaxonomy(id),
    onSuccess: invalidate,
  });
}

// ── Settings ────────────────────────────────────────────────────────────────

export function useAdminSettings(): UseQueryResult<AdminSettings> {
  return useQuery({
    queryKey: queryKeys.admin.settings(),
    queryFn: () => adminApi.getSettings(),
  });
}

/** Seeds the settings cache from a save's own response. */
function useApplySettings(): (settings: AdminSettings) => void {
  const queryClient = useQueryClient();
  return (settings: AdminSettings) => {
    queryClient.setQueryData(queryKeys.admin.settings(), settings);
  };
}

export function useSaveBandThresholds(): UseMutationResult<
  AdminSettings,
  Error,
  SaveBandThresholdsRequest
> {
  const apply = useApplySettings();
  return useMutation({
    mutationFn: (body: SaveBandThresholdsRequest) => adminApi.saveBandThresholds(body),
    onSuccess: apply,
  });
}

export function useSaveStallThresholds(): UseMutationResult<
  AdminSettings,
  Error,
  SaveStallThresholdsRequest
> {
  const apply = useApplySettings();
  return useMutation({
    mutationFn: (body: SaveStallThresholdsRequest) => adminApi.saveStallThresholds(body),
    onSuccess: apply,
  });
}

/* No `useSaveScorecardCriteria` hook yet: the settings screen shows the
   criteria weights read-only (a safe weight editor needs the sum-to-one guard
   the API enforces, and a half-built one that could save an invalid set is
   worse than a clear display). The api request exists for when that editor
   lands; adding the hook before its UI would be dead code the coverage gate
   would rightly flag. */

// ── Users ─────────────────────────────────────────────────────────────────────

export function useAdminUsers(): UseQueryResult<AdminUser[]> {
  return useQuery({
    queryKey: queryKeys.admin.users(),
    queryFn: () => adminApi.listUsers(),
  });
}

function useInvalidateUsers(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
  };
}

export function useChangeUserRole(): UseMutationResult<
  AdminUser,
  Error,
  { id: string; role: string }
> {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      adminApi.changeUserRole(id, role),
    onSuccess: invalidate,
  });
}

export function useSetUserActive(): UseMutationResult<
  AdminUser,
  Error,
  { id: string; isActive: boolean }
> {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.setUserActive(id, isActive),
    onSuccess: invalidate,
  });
}

// ── Demo data ───────────────────────────────────────────────────────────────

/** Load or clear touches every entity, so the whole cache is invalidated. */
function useInvalidateAll(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries();
  };
}

export function useLoadDemoData(): UseMutationResult<{ affected: number }, Error, void> {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: () => adminApi.loadDemoData(),
    onSuccess: invalidate,
  });
}

export function useClearDemoData(): UseMutationResult<{ affected: number }, Error, void> {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: () => adminApi.clearDemoData(),
    onSuccess: invalidate,
  });
}
